import { GAME_CONFIG, type KeyValueStore } from '../core';
import { storedProfile } from '../core/meta';

const SEEN_KEY = 'critter.coach';
const REPLAY_KEY = 'critter.coach.replay';

export interface CoachStep {
  title: string;
  body: string;
}

/** Four beats on the board. The parchment voice, and nothing else. */
export const COACH_STEPS: readonly CoachStep[] = [
  {
    title: 'Swap to match',
    body: 'Drag a tile onto a neighbor. Three in a line clear.',
  },
  {
    title: 'A line of four',
    body: 'Four in a line makes a stripe or a bee, and you take another turn. Match four again to keep going. Double-tap either to set it off.',
  },
  {
    title: 'Meter and tricks',
    body: 'Matches fill the honey meter. A full meter is your signature. Firing a trick spends one move, and you can still swap. A line of four gives that move back. Hold a disc to read it.',
  },
  {
    title: 'Rounds',
    body: 'Each critter gets four rounds of five moves. The higher score wins the glen.',
  },
];

export function coachSeen(store: KeyValueStore): boolean {
  return store.getItem(SEEN_KEY) === 'seen';
}

/**
 * The lesson plays on the first duel only: ranked, practice, and rematch.
 * Settings can ask for one more. A saved `tutorialSeen` wins over the old coach key.
 */
export function coachDue(store: KeyValueStore): boolean {
  if (store.getItem(REPLAY_KEY) === '1') return true;
  const profile = storedProfile(store);
  if (profile && typeof profile.tutorialSeen === 'boolean') return !profile.tutorialSeen;
  return !coachSeen(store);
}

export function markCoachSeen(store: KeyValueStore): void {
  store.setItem(SEEN_KEY, 'seen');
  store.setItem(REPLAY_KEY, '0');
}

/** Settings asks the next duel to teach again. */
export function requestCoachReplay(store: KeyValueStore): void {
  store.setItem(REPLAY_KEY, '1');
}

export function coachStepCount(): number {
  return COACH_STEPS.length;
}

const COUNT_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];

/**
 * The lesson for this match length. The default 4×5 copy is the constant
 * above, so a standard duel still says four rounds of five moves.
 */
export function coachScript(
  movesPerTurn: number,
  turnsPerPlayer: number,
  ruleset?: 'charm',
): readonly CoachStep[] {
  if (
    ruleset !== 'charm' &&
    movesPerTurn === GAME_CONFIG.movesPerTurn &&
    turnsPerPlayer === GAME_CONFIG.turnsPerPlayer
  ) {
    return COACH_STEPS;
  }
  const rounds = COUNT_WORD[turnsPerPlayer] ?? String(turnsPerPlayer);
  const moves = COUNT_WORD[movesPerTurn] ?? String(movesPerTurn);
  return COACH_STEPS.map((step) => {
    if (step.title === 'Rounds') {
      return {
        ...step,
        body: `Each critter gets ${rounds} rounds of ${moves} moves. The higher score wins the glen.`,
      };
    }
    if (ruleset === 'charm' && step.title === 'Meter and tricks') {
      return {
        ...step,
        body: 'Tiles of your color fill the Charm. A full meter fires it and does not spend a move. Each Trick fires once the same way.',
      };
    }
    return step;
  });
}
